import { Button, CircularProgress } from '@civilgrid/ui/components';
import {
  DEFAULT_TIER_ENABLED,
  FEATURE_DEFAULTS,
  type FeatureBillingTier,
  NON_CONFIGURABLE_FEATURES,
  type ProjectFeature,
} from '@civilgrid/utils/src/common/featureAccess';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
  updateOrganizationSubscription as apiUpdateOrganizationSubscription,
  getOrganization,
  getOrganizationSubscriptions,
} from '~/api/internal';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { OrganizationDetail, Subscription, SubscriptionDetail } from '~/common/types';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import Form from '~/components2/forms/Form';
import { Notify } from '~/components2/Notify';
import OrganizationPicker from '~/components2/OrganizationPicker';
import { toNumber, validateDecimal } from '../common/common';
import { MajorHeading } from '../common/Heading';
import ConnectbaseLookupToggle from './ConnectbaseLookupToggle';
import FeatureAccessEditor, { type ViewMode } from './FeatureAccessEditor';
import { SubscriptionHistoryList } from './SubscriptionDetailsView';

type TFormValues = {
  organizationId: string;
  projectCost: Subscription['projectCost'];
  sfdcAccountId: string;
  sfdcOpportunityId: string;
  featureAccess: Partial<Record<string, FeatureBillingTier[]>>;
  enabledBillingTiers: Partial<Record<FeatureBillingTier, boolean>>;
};

interface ComponentState {
  selectedOrganization: OrganizationDetail | null;
  subscriptions: SubscriptionDetail[];
  isLoadingSubscription: boolean;
  isProcessing: boolean;
}

const ZEROED_UNIT_COST = { urban: { unit: 0, cost: 0 }, rural: { unit: 0, cost: 0 } };
const ZEROED_TIER_COST = {
  polygon: ZEROED_UNIT_COST,
  linear: ZEROED_UNIT_COST,
  intersection: ZEROED_UNIT_COST,
  circle: ZEROED_UNIT_COST,
};
// Zeroed pricing baseline — used for the form default and as the reset fallback when switching to an
// org whose active subscription has no projectCost (so we never carry over the previous org's pricing).
const ZEROED_PROJECT_COST: Subscription['projectCost'] = {
  proposal: ZEROED_TIER_COST,
  project: ZEROED_TIER_COST,
  projectPlus: ZEROED_TIER_COST,
};

export default function UpdateOrganizationSubscription() {
  const [state, setState] = useState<ComponentState>({
    selectedOrganization: null,
    subscriptions: [],
    isLoadingSubscription: false,
    isProcessing: false,
  });

  const form = useForm<TFormValues>({
    defaultValues: {
      organizationId: '',
      sfdcAccountId: '',
      sfdcOpportunityId: '',
      featureAccess: {},
      enabledBillingTiers: {},
      projectCost: ZEROED_PROJECT_COST,
    },
  });

  const [activeTab, setActiveTab] = useState<'pricing' | 'features'>('pricing');
  const [featureView, setFeatureView] = useState<ViewMode>('org');
  const availableTabs = ['pricing', 'features'] as const;

  const handleToggleTier = (tier: FeatureBillingTier, enabled: boolean) => {
    const next = { ...form.getValues('enabledBillingTiers') };
    // Store only deviations from the code default (source of truth in @civilgrid/utils).
    if (enabled === DEFAULT_TIER_ENABLED[tier]) delete next[tier];
    else next[tier] = enabled;
    form.setValue('enabledBillingTiers', next, { shouldDirty: true });
  };

  const handleToggleFeature = (feature: ProjectFeature, tier: FeatureBillingTier) => {
    const current = form.getValues('featureAccess');
    const base = current[feature] ?? FEATURE_DEFAULTS[feature];
    const updated = base.includes(tier) ? base.filter((t) => t !== tier) : [...base, tier];
    const def = FEATURE_DEFAULTS[feature];
    const matchesDefault = updated.length === def.length && def.every((t) => updated.includes(t));
    const next = { ...current };
    if (matchesDefault) delete next[feature];
    else next[feature] = updated;
    form.setValue('featureAccess', next, { shouldDirty: true });
  };

  const loadOrganizationData = async (organizationId: string) => {
    const [fullOrganization, organizationSubscriptions] = await Promise.all([
      getOrganization(organizationId),
      getOrganizationSubscriptions(organizationId),
    ]);

    setState((prev) => ({
      ...prev,
      selectedOrganization: fullOrganization,
      subscriptions: organizationSubscriptions,
      isLoadingSubscription: false,
    }));

    // Drop any non-configurable feature overrides (CivilSearch/Digitization) so they are never
    // re-persisted; their tier availability is permanently hardcoded.
    const storedFeatureAccess = fullOrganization.subscription?.featureAccess ?? {};
    const configurableFeatureAccess = Object.fromEntries(
      Object.entries(storedFeatureAccess).filter(([key]) => !NON_CONFIGURABLE_FEATURES.has(key as ProjectFeature)),
    );

    // Establish the form baseline with a single reset (not setValue) so react-hook-form rebases
    // `defaultValues` — otherwise `isDirty` compares against the blank initial defaults and the
    // "Unsaved changes" state sticks / never clears when a toggle is reverted. Fall back to the zeroed
    // pricing baseline (not the current form value) so switching orgs never carries over prior pricing.
    form.reset({
      organizationId,
      projectCost: fullOrganization.subscription?.projectCost ?? ZEROED_PROJECT_COST,
      sfdcAccountId: fullOrganization.subscription?.sfdcAccountId ?? '',
      sfdcOpportunityId: fullOrganization.subscription?.sfdcOpportunityId ?? '',
      featureAccess: configurableFeatureAccess,
      enabledBillingTiers: fullOrganization.subscription?.enabledBillingTiers ?? {},
    });
  };

  // Handle organization selection
  const handleOrganizationSelect = async (organizationId: string, _organization: OrganizationDetail) => {
    try {
      setState((prev) => ({ ...prev, isLoadingSubscription: true }));
      await loadOrganizationData(organizationId);
    } catch (error) {
      console.error('Error fetching organization details:', error);
      Notify.error({ message: 'Error loading organization details' });
      setState((prev) => ({ ...prev, isLoadingSubscription: false }));
    }
  };

  const setToZeroCostOrg = () => {
    const tiers = Object.keys(form.getValues('projectCost')) as (keyof Subscription['projectCost'])[];

    tiers.forEach((tier) => {
      const projectTypes = Object.keys(
        form.getValues(`projectCost.${tier}`),
      ) as (keyof Subscription['projectCost'][typeof tier])[];

      projectTypes.forEach((projectType) => {
        const baseKey = `projectCost.${tier}.${projectType}` as const;
        form.setValue(`${baseKey}.urban.cost`, 0, { shouldDirty: true });
        form.setValue(`${baseKey}.rural.cost`, 0, { shouldDirty: true });
      });
    });
  };

  const setLatestPricing = () => {
    form.setValue(
      'projectCost',
      {
        proposal: {
          polygon: {
            urban: { unit: 20, cost: 250_00 },
            rural: { unit: 20, cost: 250_00 },
          },
          intersection: {
            urban: { unit: 3, cost: 250_00 },
            rural: { unit: 3, cost: 250_00 },
          },
          linear: {
            urban: { unit: 0.5, cost: 250_00 },
            rural: { unit: 0.5, cost: 250_00 },
          },
          circle: {
            urban: { unit: 0.25, cost: 250_00 },
            rural: { unit: 0.25, cost: 250_00 },
          },
        },
        project: {
          polygon: {
            urban: { unit: 20, cost: 1250_00 },
            rural: { unit: 20, cost: 1250_00 },
          },
          intersection: {
            urban: { unit: 3, cost: 1250_00 },
            rural: { unit: 3, cost: 1250_00 },
          },
          linear: {
            urban: { unit: 0.5, cost: 1250_00 },
            rural: { unit: 0.5, cost: 1250_00 },
          },
          circle: {
            urban: { unit: 0.25, cost: 1250_00 },
            rural: { unit: 0.25, cost: 1250_00 },
          },
        },
        projectPlus: {
          polygon: {
            urban: { unit: 20, cost: 2500_00 },
            rural: { unit: 20, cost: 2500_00 },
          },
          intersection: {
            urban: { unit: 3, cost: 2500_00 },
            rural: { unit: 3, cost: 2500_00 },
          },
          linear: {
            urban: { unit: 0.5, cost: 2500_00 },
            rural: { unit: 0.5, cost: 2500_00 },
          },
          circle: {
            urban: { unit: 0.25, cost: 2500_00 },
            rural: { unit: 0.25, cost: 2500_00 },
          },
        },
      },
      { shouldDirty: true },
    );
  };

  const onSubmit: SubmitHandler<TFormValues> = async (data) => {
    try {
      setState((prev) => ({ ...prev, isProcessing: true }));

      // Convert form values to numbers using toNumber helper
      const processedProjectCost: Subscription['projectCost'] = {
        proposal: {
          polygon: {
            urban: {
              unit: toNumber(data.projectCost.proposal.polygon.urban.unit),
              cost: toNumber(data.projectCost.proposal.polygon.urban.cost),
            },
            rural: {
              unit: toNumber(data.projectCost.proposal.polygon.rural.unit),
              cost: toNumber(data.projectCost.proposal.polygon.rural.cost),
            },
          },
          intersection: {
            urban: {
              unit: toNumber(3),
              cost: toNumber(data.projectCost.proposal.polygon.urban.cost),
            },
            rural: {
              unit: toNumber(3),
              cost: toNumber(data.projectCost.proposal.polygon.rural.cost),
            },
          },
          linear: {
            urban: {
              unit: toNumber(data.projectCost.proposal.linear.urban.unit),
              cost: toNumber(data.projectCost.proposal.linear.urban.cost),
            },
            rural: {
              unit: toNumber(data.projectCost.proposal.linear.rural.unit),
              cost: toNumber(data.projectCost.proposal.linear.rural.cost),
            },
          },
          circle: {
            urban: {
              unit: toNumber(data.projectCost.proposal.circle.urban.unit),
              cost: toNumber(data.projectCost.proposal.circle.urban.cost),
            },
            rural: {
              unit: toNumber(data.projectCost.proposal.circle.rural.unit),
              cost: toNumber(data.projectCost.proposal.circle.rural.cost),
            },
          },
        },
        project: {
          polygon: {
            urban: {
              unit: toNumber(data.projectCost.project.polygon.urban.unit),
              cost: toNumber(data.projectCost.project.polygon.urban.cost),
            },
            rural: {
              unit: toNumber(data.projectCost.project.polygon.rural.unit),
              cost: toNumber(data.projectCost.project.polygon.rural.cost),
            },
          },
          intersection: {
            urban: {
              unit: toNumber(3),
              cost: toNumber(data.projectCost.project.polygon.urban.cost),
            },
            rural: {
              unit: toNumber(3),
              cost: toNumber(data.projectCost.project.polygon.rural.cost),
            },
          },
          linear: {
            urban: {
              unit: toNumber(data.projectCost.project.linear.urban.unit),
              cost: toNumber(data.projectCost.project.linear.urban.cost),
            },
            rural: {
              unit: toNumber(data.projectCost.project.linear.rural.unit),
              cost: toNumber(data.projectCost.project.linear.rural.cost),
            },
          },
          circle: {
            urban: {
              unit: toNumber(data.projectCost.project.circle.urban.unit),
              cost: toNumber(data.projectCost.project.circle.urban.cost),
            },
            rural: {
              unit: toNumber(data.projectCost.project.circle.rural.unit),
              cost: toNumber(data.projectCost.project.circle.rural.cost),
            },
          },
        },
        projectPlus: {
          polygon: {
            urban: {
              unit: toNumber(data.projectCost.projectPlus.polygon.urban.unit),
              cost: toNumber(data.projectCost.projectPlus.polygon.urban.cost),
            },
            rural: {
              unit: toNumber(data.projectCost.projectPlus.polygon.rural.unit),
              cost: toNumber(data.projectCost.projectPlus.polygon.rural.cost),
            },
          },
          intersection: {
            urban: {
              unit: toNumber(3),
              cost: toNumber(data.projectCost.projectPlus.polygon.urban.cost),
            },
            rural: {
              unit: toNumber(3),
              cost: toNumber(data.projectCost.projectPlus.polygon.rural.cost),
            },
          },
          linear: {
            urban: {
              unit: toNumber(data.projectCost.projectPlus.linear.urban.unit),
              cost: toNumber(data.projectCost.projectPlus.linear.urban.cost),
            },
            rural: {
              unit: toNumber(data.projectCost.projectPlus.linear.rural.unit),
              cost: toNumber(data.projectCost.projectPlus.linear.rural.cost),
            },
          },
          circle: {
            urban: {
              unit: toNumber(data.projectCost.projectPlus.circle.urban.unit),
              cost: toNumber(data.projectCost.projectPlus.circle.urban.cost),
            },
            rural: {
              unit: toNumber(data.projectCost.projectPlus.circle.rural.unit),
              cost: toNumber(data.projectCost.projectPlus.circle.rural.cost),
            },
          },
        },
      };

      const response = await apiUpdateOrganizationSubscription(
        data.organizationId,
        processedProjectCost,
        {
          sfdcAccountId: data.sfdcAccountId.trim() || null,
          sfdcOpportunityId: data.sfdcOpportunityId.trim() || null,
        },
        { featureAccess: data.featureAccess, enabledBillingTiers: data.enabledBillingTiers },
      );

      if (response.success) {
        // Only emit the feature-access event when the save actually changed feature/tier config.
        // Use dirtyFields (relative to the loaded baseline) — NOT "has any override", since the form
        // is seeded with the org's existing overrides, so a pricing-only save on an org that already
        // has overrides would otherwise fire this spuriously. Read before form.reset() clears dirtiness.
        const hasFeatureAccessChange =
          !!form.formState.dirtyFields.featureAccess || !!form.formState.dirtyFields.enabledBillingTiers;
        if (hasFeatureAccessChange) {
          captureTypedPosthogEvent(POSTHOG_EVENTS.adminSubscriptionFeatureAccessUpdated, {
            organization_id: data.organizationId,
            overridden_features: Object.keys(data.featureAccess),
            disabled_tiers: Object.entries(data.enabledBillingTiers)
              .filter(([, enabled]) => enabled === false)
              .map(([tier]) => tier),
          });
        }

        Notify.info({
          message: `Subscription updated successfully for ${state.selectedOrganization?.name}!`,
        });

        await loadOrganizationData(data.organizationId);

        // Reset form to mark as clean
        form.reset(data);
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      console.error('Error updating subscription:', error);
      Notify.error({
        message: 'Error updating subscription. Please try again.',
      });
    } finally {
      setState((prev) => ({ ...prev, isProcessing: false }));
    }
  };

  return (
    <Form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mb-10">
      {/* Organization Search Section */}
      <div className="space-y-2">
        <MajorHeading>Select Organization</MajorHeading>
        <div className="flex items-center space-x-2">
          <span className="font-semibold">Organization:</span>
          <OrganizationPicker
            selectedOrganization={state.selectedOrganization}
            onOrganizationSelect={handleOrganizationSelect}
          />
        </div>
      </div>

      {/* Subscription editor tabs */}
      {state.selectedOrganization && (
        <div className="flex gap-6 border-b border-grey-30">
          {availableTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              data-testid={`subscription-tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`-mb-px border-b-2 px-1 pb-2 text-sm ${
                activeTab === tab ? 'border-blue-60 font-medium text-blue-80' : 'border-transparent text-grey-90'
              }`}
            >
              {tab === 'pricing' ? 'Pricing' : 'Feature Provisioning'}
            </button>
          ))}
        </div>
      )}

      {/* New Subscription Form Section */}
      {state.selectedOrganization && activeTab === 'pricing' && (
        <div className="relative w-full space-y-4 overflow-hidden">
          <hr />
          <div>
            <div className="text-sm font-bold mb-2">New Subscription</div>
            <div className="space-x-2">
              <Button type="button" variant="secondary" onClick={setLatestPricing}>
                Set latest pricing
              </Button>
              <Button type="button" variant="secondary" onClick={setToZeroCostOrg}>
                Set ZERO cost
              </Button>
              <Button
                type="submit"
                loading={state.isProcessing}
                disabled={
                  state.isProcessing ||
                  !form.formState.isValid ||
                  !form.formState.isDirty ||
                  !state.selectedOrganization
                }
              >
                Update Subscription
              </Button>
            </div>
          </div>
          <ControlledInput
            control={form.control}
            name="sfdcAccountId"
            label="SFDC Account ID"
            data-testid="update-sub-sfdc-account-id"
          />
          <ControlledInput
            control={form.control}
            name="sfdcOpportunityId"
            label="SFDC Opportunity ID"
            data-testid="update-sub-sfdc-opportunity-id"
          />
          <MajorHeading>Project Lite Pricing</MajorHeading>

          <div className="flex space-x-4">
            <div className="flex w-1/4 flex-col space-y-2">
              <MajorHeading>Polygon</MajorHeading>
              <ControlledInput
                control={form.control}
                name="projectCost.proposal.polygon.urban.unit"
                isRequired
                label="Polygon Units (acres) - Urban"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.proposal.polygon.urban.cost"
                isRequired
                label="Polygon Unit Cost (cents) - Urban"
                validate={validateDecimal}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.proposal.polygon.rural.unit"
                isRequired
                label="Polygon Units (acres) - Rural"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.proposal.polygon.rural.cost"
                isRequired
                label="Polygon Unit Cost (cents) - Rural"
                validate={validateDecimal}
              />
            </div>

            <div className="flex w-1/4 flex-col space-y-2">
              <MajorHeading>Linear</MajorHeading>
              <ControlledInput
                control={form.control}
                name="projectCost.proposal.linear.urban.unit"
                isRequired
                label="Linear Units (miles) - Urban"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.proposal.linear.urban.cost"
                isRequired
                label="Linear Unit Cost (cents) - Urban"
                validate={validateDecimal}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.proposal.linear.rural.unit"
                isRequired
                label="Linear Units (miles) - Rural"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.proposal.linear.rural.cost"
                isRequired
                label="Linear Unit Cost (cents) - Rural"
                validate={validateDecimal}
              />
            </div>

            <div className="flex w-1/4 flex-col space-y-2">
              <MajorHeading>Circle</MajorHeading>
              <ControlledInput
                control={form.control}
                name="projectCost.proposal.circle.urban.unit"
                isRequired
                label="Circle Units (acres) - Urban"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.proposal.circle.urban.cost"
                isRequired
                label="Circle Unit Cost (cents) - Urban"
                validate={validateDecimal}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.proposal.circle.rural.unit"
                isRequired
                label="Circle Units (acres) - Rural"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.proposal.circle.rural.cost"
                isRequired
                label="Circle Unit Cost (cents) - Rural"
                validate={validateDecimal}
              />
            </div>
          </div>

          <hr />
          <MajorHeading>Project Pricing</MajorHeading>

          <div className="flex space-x-4">
            <div className="flex w-1/4 flex-col space-y-2">
              <MajorHeading>Polygon</MajorHeading>
              <ControlledInput
                control={form.control}
                name="projectCost.project.polygon.urban.unit"
                isRequired
                label="Polygon Units (acres) - Urban"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.project.polygon.urban.cost"
                isRequired
                label="Polygon Unit Cost (cents) - Urban"
                validate={validateDecimal}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.project.polygon.rural.unit"
                isRequired
                label="Polygon Units (acres) - Rural"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.project.polygon.rural.cost"
                isRequired
                label="Polygon Unit Cost (cents) - Rural"
                validate={validateDecimal}
              />
            </div>

            <div className="flex w-1/4 flex-col space-y-2">
              <MajorHeading>Linear</MajorHeading>
              <ControlledInput
                control={form.control}
                name="projectCost.project.linear.urban.unit"
                isRequired
                label="Linear Units (miles) - Urban"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.project.linear.urban.cost"
                isRequired
                label="Linear Unit Cost (cents) - Urban"
                validate={validateDecimal}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.project.linear.rural.unit"
                isRequired
                label="Linear Units (miles) - Rural"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.project.linear.rural.cost"
                isRequired
                label="Linear Unit Cost (cents) - Rural"
                validate={validateDecimal}
              />
            </div>

            <div className="flex w-1/4 flex-col space-y-2">
              <MajorHeading>Circle</MajorHeading>
              <ControlledInput
                control={form.control}
                name="projectCost.project.circle.urban.unit"
                isRequired
                label="Circle Units (acres) - Urban"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.project.circle.urban.cost"
                isRequired
                label="Circle Unit Cost (cents) - Urban"
                validate={validateDecimal}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.project.circle.rural.unit"
                isRequired
                label="Circle Units (acres) - Rural"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.project.circle.rural.cost"
                isRequired
                label="Circle Unit Cost (cents) - Rural"
                validate={validateDecimal}
              />
            </div>
          </div>

          <hr />
          <MajorHeading>Project Plus Pricing</MajorHeading>

          <div className="flex space-x-4">
            <div className="flex w-1/4 flex-col space-y-2">
              <MajorHeading>Polygon</MajorHeading>
              <ControlledInput
                control={form.control}
                name="projectCost.projectPlus.polygon.urban.unit"
                isRequired
                label="Polygon Units (acres) - Urban"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.projectPlus.polygon.urban.cost"
                isRequired
                label="Polygon Unit Cost (cents) - Urban"
                validate={validateDecimal}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.projectPlus.polygon.rural.unit"
                isRequired
                label="Polygon Units (acres) - Rural"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.projectPlus.polygon.rural.cost"
                isRequired
                label="Polygon Unit Cost (cents) - Rural"
                validate={validateDecimal}
              />
            </div>

            <div className="flex w-1/4 flex-col space-y-2">
              <MajorHeading>Linear</MajorHeading>
              <ControlledInput
                control={form.control}
                name="projectCost.projectPlus.linear.urban.unit"
                isRequired
                label="Linear Units (miles) - Urban"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.projectPlus.linear.urban.cost"
                isRequired
                label="Linear Unit Cost (cents) - Urban"
                validate={validateDecimal}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.projectPlus.linear.rural.unit"
                isRequired
                label="Linear Units (miles) - Rural"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.projectPlus.linear.rural.cost"
                isRequired
                label="Linear Unit Cost (cents) - Rural"
                validate={validateDecimal}
              />
            </div>

            <div className="flex w-1/4 flex-col space-y-2">
              <MajorHeading>Circle</MajorHeading>
              <ControlledInput
                control={form.control}
                name="projectCost.projectPlus.circle.urban.unit"
                isRequired
                label="Circle Units (acres) - Urban"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.projectPlus.circle.urban.cost"
                isRequired
                label="Circle Unit Cost (cents) - Urban"
                validate={validateDecimal}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.projectPlus.circle.rural.unit"
                isRequired
                label="Circle Units (acres) - Rural"
                validate={{
                  positiveNumber: (value: string) => {
                    const num = parseFloat(value);
                    return num > 0 || 'Must be a positive number';
                  },
                }}
              />

              <ControlledInput
                control={form.control}
                name="projectCost.projectPlus.circle.rural.cost"
                isRequired
                label="Circle Unit Cost (cents) - Rural"
                validate={validateDecimal}
              />
            </div>
          </div>
        </div>
      )}

      {/* Feature Provisioning Section */}
      {state.selectedOrganization && activeTab === 'features' && (
        <div className="space-y-4">
          <FeatureAccessEditor
            viewMode={featureView}
            onViewModeChange={setFeatureView}
            orgName={state.selectedOrganization.name}
            featureAccess={form.watch('featureAccess')}
            enabledBillingTiers={form.watch('enabledBillingTiers')}
            onToggleFeature={handleToggleFeature}
            onToggleTier={handleToggleTier}
            saveSlot={
              <div className="flex items-center gap-3">
                <Button
                  type="submit"
                  data-testid="feature-provisioning-save"
                  loading={state.isProcessing}
                  disabled={
                    state.isProcessing ||
                    !form.formState.isValid ||
                    !form.formState.isDirty ||
                    !state.selectedOrganization
                  }
                >
                  Save
                </Button>
                {form.formState.isDirty && <span className="text-sm text-yellow-60">Unsaved changes</span>}
              </div>
            }
          />
          {/* Keyed by org so picking a different one remounts with that org's stored flag. */}
          <ConnectbaseLookupToggle
            key={state.selectedOrganization.id}
            organizationId={state.selectedOrganization.id}
            connectbaseLookupEnabled={state.selectedOrganization.connectbaseLookupEnabled}
          />
        </div>
      )}

      {/* Subscription History Section — kept at the bottom, below the feature provisioning matrix */}
      {state.selectedOrganization && (
        <div className="space-y-4">
          <MajorHeading>Subscription History</MajorHeading>
          {state.isLoadingSubscription ? (
            <CircularProgress indeterminate />
          ) : (
            <SubscriptionHistoryList subscriptions={state.subscriptions} />
          )}
        </div>
      )}
    </Form>
  );
}
